import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useMemo, useRef, useEffect } from 'react';
import useControllableState from '../../hooks/use-controllable.esm.js';
import { __DEV__ } from '../../utils/assertion.esm.js';
import { createContext } from '../../utils/react-helpers.esm.js';
import Subject from '../_utils/event-observer.esm.js';

var _createContext = createContext({
    name: 'TabsContext'
  }),
  _createContext2 = _slicedToArray(_createContext, 2),
  TabsProvider = _createContext2[0],
  useTabsContext = _createContext2[1];
var defaultScrollIntoViewOptions = {
  behavior: 'smooth',
  block: 'start'
};
var TabsContextProvider = function TabsContextProvider(props) {
  var _props$initialSelecti = props.initialSelection,
    initialSelection = _props$initialSelecti === void 0 ? 0 : _props$initialSelecti,
    selectionProp = props.selection,
    onChange = props.onChange,
    variant = props.variant,
    children = props.children,
    _props$mode = props.mode,
    mode = _props$mode === void 0 ? 'switch' : _props$mode,
    _props$scrollIntoView = props.scrollIntoViewOptions,
    scrollIntoViewOptions = _props$scrollIntoView === void 0 ? defaultScrollIntoViewOptions : _props$scrollIntoView;
  var _useControllableState = _slicedToArray(useControllableState({
      value: selectionProp,
      defaultValue: initialSelection,
      onChange: onChange
    }).state, 2),
    selection = _useControllableState[0],
    setSelection = _useControllableState[1];
  var selectionChangedObserver = useMemo(function () {
    return new Subject('tabs-selection-changed');
  }, []);
  var isScrolling = useRef(false);
  var isSettingTab = useRef(false);
  var scrollTimer = useRef();
  var settingTabTimer = useRef();
  useEffect(function () {
    var handleSelectionChange = function handleSelectionChange(event) {
      if (mode === 'stack' && event.source === 'tabs') {
        var _document$getElementB;
        setSelection(event.value);
        (_document$getElementB = document.getElementById("nav-scrollto-".concat(event.value))) === null || _document$getElementB === void 0 ? void 0 : _document$getElementB.scrollIntoView(scrollIntoViewOptions === undefined || scrollIntoViewOptions === true ? defaultScrollIntoViewOptions : scrollIntoViewOptions);
        scrollTimer.current && clearTimeout(scrollTimer.current);
        isScrolling.current = true;
        scrollTimer.current = setTimeout(function () {
          isScrolling.current = false;
        }, 1000);
      }
      if (mode === 'switch' || mode === 'stack' && event.source === 'panels' && !isScrolling.current) {
        // if we receive multiple events within a 50ms period, only handle the first one
        if (isSettingTab.current) return;
        isSettingTab.current = true;
        settingTabTimer.current = setTimeout(function () {
          isSettingTab.current = false;
        }, 50);
        setSelection(event.value);
      }
    };
    selectionChangedObserver.attach(handleSelectionChange);
    return function () {
      return selectionChangedObserver.detach(handleSelectionChange);
    };
  }, [selectionChangedObserver, setSelection, mode, scrollIntoViewOptions]);
  var context = useMemo(function () {
    return {
      variant: variant,
      selectionChangedObserver: selectionChangedObserver,
      selection: selection,
      controlledSelection: selection,
      mode: mode
    };
  }, [variant, selectionChangedObserver, selection, mode]);
  useEffect(function () {
    return function () {
      scrollTimer.current && clearTimeout(scrollTimer.current);
      settingTabTimer.current && clearTimeout(settingTabTimer.current);
    };
  }, []);
  return /*#__PURE__*/React.createElement(TabsProvider, {
    value: context
  }, children);
};
if (__DEV__) {
  TabsContextProvider.displayName = 'TabsContextProvider';
}

export { TabsContextProvider, useTabsContext };
//# sourceMappingURL=tabs-context.esm.js.map
